import type {
  OtpClientCreatedView,
  OtpClientStatsView,
  OtpClientView,
  OtpMessageSource,
  OtpPaymentView,
  OtpServiceMessageView,
  OtpServiceUsageView,
  WaInstance,
} from '@erp/shared';
import Decimal from 'decimal.js';
import {
  ArrowLeft,
  BookOpen,
  Building2,
  CalendarDays,
  Coins,
  Contact,
  Download,
  Edit3,
  Eye,
  EyeOff,
  Mail,
  MessageCircle,
  PauseCircle,
  Phone,
  Play,
  ReceiptText,
  RefreshCw,
  Send,
  Settings2,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Button as ShButton,
  Separator,
  Skeleton,
} from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  CopyButton,
  CopyableCode,
  DataTable,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { formatDate, formatDateTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';
import { CHART_COLORS } from '../reports/_shared/chartColors.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

import {
  buildOtpClientPayload,
  DEFAULT_OTP_CLIENT_VALUES,
  OTP_PRICE_RE,
  OtpClientForm,
  type OtpClientFormValues,
  useOtpFormErrorMessage,
} from './_shared/OtpClientForm.js';
import { OtpKeyRevealDialog } from './_shared/OtpKeyRevealDialog.js';
import {
  buildPostmanCollection,
  downloadJson,
  fmtUsd,
  instanceStateVariant,
  maskPhone,
  OTP_BASE_URL,
  OTP_DOCS_URL,
} from './_shared/OtpShared.js';

const MSG_PAGE_SIZE = 25;

function chartTooltipStyle(): React.CSSProperties {
  return {
    background: CHART_COLORS.bg,
    border: `1px solid ${CHART_COLORS.border}`,
    borderRadius: 6,
    fontSize: 12,
    color: CHART_COLORS.fg,
  };
}

/**
 * One OTP client: usage + billing rollup, the 30-day delivery chart, the
 * delivery settings, a customer hand-off card, manual payments, and the raw
 * message ledger. Mutations (edit / rotate / toggle / payments) refetch the
 * whole billing picture — the stats and usage aggregates all shift together.
 */
export function PlatformOtpClientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('platform');
  const { t: tc } = useTranslation();

  const [client, setClient] = useState<OtpClientView | null>(null);
  const [stats, setStats] = useState<OtpClientStatsView | null>(null);
  const [usage, setUsage] = useState<OtpServiceUsageView | null>(null);
  const [payments, setPayments] = useState<OtpPaymentView[] | null>(null);
  const [instances, setInstances] = useState<WaInstance[] | null>(null);
  const [messages, setMessages] = useState<{ items: OtpServiceMessageView[]; total: number } | null>(null);
  const [msgPage, setMsgPage] = useState(1);
  const [revealedPhones, setRevealedPhones] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState<OtpClientFormValues>({ ...DEFAULT_OTP_CLIENT_VALUES });
  const [busy, setBusy] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [payForm, setPayForm] = useState({ amount: '', note: '' });
  const [payBusy, setPayBusy] = useState(false);
  const formError = useOtpFormErrorMessage();

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const [c, u, p] = await Promise.all([
        platformApi().get<{ client: OtpClientView; stats: OtpClientStatsView }>(
          `/otp-service/clients/${id}`,
        ),
        platformApi().get<OtpServiceUsageView>(`/otp-service/clients/${id}/usage`),
        platformApi().get<{ items: OtpPaymentView[] }>(`/otp-service/clients/${id}/payments`),
      ]);
      setClient(c.data.client);
      setStats(c.data.stats);
      setUsage(u.data);
      setPayments(p.data.items);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [id]);

  const loadMessages = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const r = await platformApi().get<{ items: OtpServiceMessageView[]; total: number }>(
        `/otp-service/clients/${id}/messages`,
        { params: { page: msgPage, pageSize: MSG_PAGE_SIZE } },
      );
      setMessages(r.data);
    } catch {
      setMessages({ items: [], total: 0 });
    }
  }, [id, msgPage]);

  // Everything fires in parallel on mount; messages also refetch on page flips.
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    let cancelled = false;
    void platformApi()
      .get<{ items: WaInstance[] }>('/whatsapp/instances')
      .then((r) => { if (!cancelled) setInstances(r.data.items); })
      .catch(() => { if (!cancelled) setInstances([]); });
    return () => { cancelled = true; };
  }, []);


  const openEdit = (): void => {
    if (!client) return;
    setEditValues({
      name: client.name,
      company: client.company ?? '',
      contactEmail: client.contactEmail ?? '',
      contactPhone: client.contactPhone ?? '',
      instanceName: client.instanceName ?? '',
      // "0.0100" → "0.01" so the operator edits the natural form.
      pricePerMessage: new Decimal(client.pricePerMessage || 0).toString(),
      messageTemplate: client.messageTemplate,
      codeLength: client.codeLength,
      ttlSeconds: String(client.ttlSeconds),
    });
    setEditOpen(true);
  };

  const saveEdit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!id) return;
    const built = buildOtpClientPayload(editValues);
    if (!built.ok) {
      toast.error(formError(built.error));
      return;
    }
    setBusy(true);
    try {
      await platformApi().patch(`/otp-service/clients/${id}`, built.payload);
      setEditOpen(false);
      await load();
    } catch {
      toast.error(t('otpService.updateError', { defaultValue: 'Could not save the changes.' }));
    } finally {
      setBusy(false);
    }
  };

  const onRotate = async (): Promise<void> => {
    if (!id || !client) return;
    const ok = await confirmDialog({
      message: t('otpService.detail.rotateConfirm', {
        defaultValue:
          'Rotate the API key for {{name}}? The current key stops working immediately — every integration must switch to the new one.',
        name: client.name,
      }),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await platformApi().post<OtpClientCreatedView>(
        `/otp-service/clients/${id}/rotate-key`,
        {},
      );
      setRevealKey(r.data.apiKey);
      await load();
    } catch {
      toast.error(t('otpService.detail.rotateError', { defaultValue: 'Could not rotate the key.' }));
    } finally {
      setBusy(false);
    }
  };

  const onToggleActive = async (): Promise<void> => {
    if (!id || !client) return;
    if (client.isActive) {
      const ok = await confirmDialog({
        message: t('otpService.detail.deactivateConfirm', {
          defaultValue:
            'Deactivate {{name}}? Their API calls are rejected until the client is reactivated.',
          name: client.name,
        }),
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await platformApi().patch(`/otp-service/clients/${id}`, { isActive: !client.isActive });
      await load();
    } catch {
      toast.error(t('otpService.detail.statusError', { defaultValue: 'Could not update the status.' }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Unlock (or revoke) the general message API for this client.
   *
   * Separate from Deactivate on purpose: is_active also stops the account's OTP
   * traffic, so it is not a lever anyone can pull on a customer whose login flow
   * is live. This one stops free-text sending and nothing else.
   */
  const onToggleMessaging = async (): Promise<void> => {
    if (!id || !client) return;
    const ok = await confirmDialog({
      message: client.messagingEnabled
        ? t('otpService.detail.messagingOffConfirm', {
            defaultValue:
              'Turn off general messaging for {{name}}? Their /messages/send calls are rejected; OTP keeps working.',
            name: client.name,
          })
        : t('otpService.detail.messagingOnConfirm', {
            defaultValue:
              'Allow {{name}} to send arbitrary WhatsApp messages from their own number, billed at their per-message price?',
            name: client.name,
          }),
      tone: client.messagingEnabled ? 'danger' : 'default',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await platformApi().patch(`/otp-service/clients/${id}`, { messagingEnabled: !client.messagingEnabled });
      await load();
    } catch {
      toast.error(t('otpService.detail.statusError', { defaultValue: 'Could not update the status.' }));
    } finally {
      setBusy(false);
    }
  };

  const onRecordPayment = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!id) return;
    const amount = payForm.amount.trim();
    if (!OTP_PRICE_RE.test(amount) || Number(amount) <= 0) {
      toast.error(t('otpService.detail.payments.amountInvalid', {
        defaultValue: 'Enter an amount greater than zero (up to 4 decimals).',
      }));
      return;
    }
    setPayBusy(true);
    try {
      await platformApi().post(`/otp-service/clients/${id}/payments`, {
        amount,
        note: payForm.note.trim() ? payForm.note.trim() : null,
      });
      setPayForm({ amount: '', note: '' });
      toast.success(t('otpService.detail.payments.recorded', { defaultValue: 'Payment recorded.' }));
      await load();
    } catch {
      toast.error(t('otpService.detail.payments.recordError', {
        defaultValue: 'Could not record the payment.',
      }));
    } finally {
      setPayBusy(false);
    }
  };

  const onDeletePayment = async (p: OtpPaymentView): Promise<void> => {
    if (!id) return;
    const ok = await confirmDialog({
      message: t('otpService.detail.payments.deleteConfirm', {
        defaultValue: "Delete this payment of {{amount}}? The client's balance goes back up.",
        amount: fmtUsd(p.amount),
      }),
      tone: 'danger',
    });
    if (!ok) return;
    setPayBusy(true);
    try {
      await platformApi().delete(`/otp-service/clients/${id}/payments/${p.id}`);
      await load();
    } catch {
      toast.error(t('otpService.detail.payments.deleteError', {
        defaultValue: 'Could not delete the payment.',
      }));
    } finally {
      setPayBusy(false);
    }
  };

  if (!client || !stats) {
    return (
      <div className="flex flex-col gap-4">
        {error && (
          <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <Card className="p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-4 h-32 w-full" />
        </Card>
      </div>
    );
  }

  const balance = new Decimal(stats.balance || 0);
  const balanceTone = balance.gt(0) ? 'danger' : balance.lt(0) ? 'success' : 'neutral';
  const balanceHint = balance.gt(0)
    ? t('otpService.detail.kpi.owes', { defaultValue: 'The customer owes this amount' })
    : balance.lt(0)
      ? t('otpService.detail.kpi.credit', { defaultValue: 'In credit — the customer overpaid' })
      : t('otpService.detail.kpi.settled', { defaultValue: 'Settled' });

  const liveInstance = client.instanceName
    ? instances?.find((i) => i.name === client.instanceName)
    : undefined;
  const instanceGone = Boolean(client.instanceName) && instances !== null && !liveInstance;

  const chartEmpty =
    !usage || usage.days.length === 0 || usage.days.every((d) => d.sent === 0 && d.failed === 0);

  const msgFrom = messages && messages.total > 0 ? (msgPage - 1) * MSG_PAGE_SIZE + 1 : 0;
  const msgTo = messages ? Math.min(messages.total, msgFrom + messages.items.length - 1) : 0;

  const sourceLabel: Record<OtpMessageSource, string> = {
    api: t('otpService.detail.messages.sourceApi', { defaultValue: 'OTP API' }),
    message: t('otpService.detail.messages.sourceMessage', { defaultValue: 'Message API' }),
    erp: t('otpService.detail.messages.sourceErp', { defaultValue: 'ERP login' }),
    test: t('otpService.detail.messages.sourceTest', { defaultValue: 'Test' }),
  };

  const messageColumns: DataTableColumn<OtpServiceMessageView>[] = [
    {
      id: 'when',
      header: t('otpService.detail.messages.when', { defaultValue: 'When' }),
      cellClassName: 'text-xs text-fg-muted whitespace-nowrap',
      cell: (m) => formatDateTime(m.createdAt, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'medium' }),
    },
    {
      id: 'phone',
      header: t('otpService.detail.messages.phone', { defaultValue: 'Phone' }),
      cell: (m) => (
        <div className="flex items-center gap-1.5">
          <span dir="ltr" className="font-mono text-xs">
            {revealedPhones[m.id] ? m.phone : maskPhone(m.phone)}
          </span>
          <button
            type="button"
            onClick={() => setRevealedPhones((r) => ({ ...r, [m.id]: !r[m.id] }))}
            className="text-fg-muted transition-colors hover:text-fg"
            aria-label={revealedPhones[m.id]
              ? t('otpService.detail.messages.hide', { defaultValue: 'Hide phone' })
              : t('otpService.detail.messages.reveal', { defaultValue: 'Reveal phone' })}
          >
            {revealedPhones[m.id]
              ? <EyeOff className="h-3.5 w-3.5" aria-hidden />
              : <Eye className="h-3.5 w-3.5" aria-hidden />}
          </button>
          <CopyButton value={m.phone} subject={t('otpService.detail.messages.phone', { defaultValue: 'Phone' })} />
        </div>
      ),
    },
    {
      id: 'source',
      header: t('otpService.detail.messages.source', { defaultValue: 'From' }),
      cell: (m) => <Badge variant="secondary">{sourceLabel[m.source]}</Badge>,
    },
    {
      id: 'status',
      header: t('otpService.detail.messages.status', { defaultValue: 'Status' }),
      cell: (m) => (
        <Badge variant={m.status === 'sent' ? 'success' : 'danger'}>
          {m.status === 'sent'
            ? t('otpService.detail.sent', { defaultValue: 'Sent' })
            : t('otpService.detail.failed', { defaultValue: 'Failed' })}
        </Badge>
      ),
    },
    {
      id: 'error',
      header: t('otpService.detail.messages.error', { defaultValue: 'Error' }),
      cellClassName: 'text-xs text-fg-muted',
      cell: (m) => m.error ?? '—',
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/platform/otp-service"
          className="inline-flex items-center gap-1 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
        >
          <ArrowLeft className="h-3 w-3 rtl:rotate-180" aria-hidden />
          {t('otpService.title', { defaultValue: 'OTP service' })}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{client.name}</h1>
            <Badge variant={client.isActive ? 'success' : 'secondary'}>
              {client.isActive
                ? t('otpService.statusActive', { defaultValue: 'Active' })
                : t('otpService.statusDisabled', { defaultValue: 'Disabled' })}
            </Badge>
            {client.messagingEnabled && (
              <Badge variant="secondary">
                {t('otpService.messagingBadge', { defaultValue: 'Messaging on' })}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-fg-muted">
            <span className="flex items-center gap-1">
              <code dir="ltr" className="font-mono">{client.keyPrefix}…</code>
              <CopyButton value={client.keyPrefix} subject={t('otpService.detail.handoff.keyPrefix', { defaultValue: 'Key prefix' })} />
            </span>
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" aria-hidden />
              {t('otpService.detail.createdAt', {
                defaultValue: 'Created {{date}}',
                date: formatDate(client.createdAt, { locale: i18n.language }),
              })}
            </span>
            {client.lastUsedAt && (
              <span>
                {t('otpService.detail.lastUsed', {
                  defaultValue: 'Last used {{date}}',
                  date: formatDateTime(client.lastUsedAt, { locale: i18n.language }),
                })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ShButton variant="outline" onClick={openEdit} disabled={busy}>
            <Edit3 className="h-4 w-4" />
            {t('otpService.detail.edit', { defaultValue: 'Edit' })}
          </ShButton>
          <ShButton variant="outline" onClick={() => void onRotate()} disabled={busy}>
            <RefreshCw className="h-4 w-4" />
            {t('otpService.detail.rotate', { defaultValue: 'Rotate key' })}
          </ShButton>
          <ShButton variant="outline" onClick={() => void onToggleMessaging()} disabled={busy}>
            <MessageCircle className="h-4 w-4" />
            {client.messagingEnabled
              ? t('otpService.detail.messagingDisable', { defaultValue: 'Disable messaging' })
              : t('otpService.detail.messagingEnable', { defaultValue: 'Enable messaging' })}
          </ShButton>
          <ShButton variant="outline" onClick={() => void onToggleActive()} disabled={busy}>
            {client.isActive ? <PauseCircle className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {client.isActive
              ? t('otpService.detail.deactivate', { defaultValue: 'Deactivate' })
              : t('otpService.detail.activate', { defaultValue: 'Activate' })}
          </ShButton>
        </div>
      </header>

      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      {/* Contact */}
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
            <Contact className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.detail.contact.title', { defaultValue: 'Contact' })}
            </h2>
          </div>
        </div>
        <Separator className="my-4" />
        {!client.company && !client.contactEmail && !client.contactPhone ? (
          <p className="text-sm text-fg-muted">
            {t('otpService.detail.contact.none', { defaultValue: 'No contact details.' })}
          </p>
        ) : (
          <dl className="flex flex-col gap-3 text-sm">
            {client.company && (
              <div className="flex items-center justify-between gap-3">
                <dt className="flex items-center gap-1.5 text-fg-muted">
                  <Building2 className="h-3.5 w-3.5" aria-hidden />
                  {t('otpService.detail.contact.company', { defaultValue: 'Company' })}
                </dt>
                <dd className="min-w-0 truncate text-end">{client.company}</dd>
              </div>
            )}
            {client.contactEmail && (
              <div className="flex items-center justify-between gap-3">
                <dt className="flex items-center gap-1.5 text-fg-muted">
                  <Mail className="h-3.5 w-3.5" aria-hidden />
                  {t('otpService.detail.contact.email', { defaultValue: 'Email' })}
                </dt>
                <dd className="flex min-w-0 items-center gap-1">
                  <a
                    href={`mailto:${client.contactEmail}`}
                    dir="ltr"
                    className="min-w-0 truncate text-brand underline-offset-2 hover:underline"
                  >
                    {client.contactEmail}
                  </a>
                  <CopyButton value={client.contactEmail} subject={t('otpService.detail.contact.email', { defaultValue: 'Email' })} />
                </dd>
              </div>
            )}
            {client.contactPhone && (
              <div className="flex items-center justify-between gap-3">
                <dt className="flex items-center gap-1.5 text-fg-muted">
                  <Phone className="h-3.5 w-3.5" aria-hidden />
                  {t('otpService.detail.contact.phone', { defaultValue: 'Phone' })}
                </dt>
                <dd dir="ltr" className="flex items-center gap-1 font-mono text-xs">
                  {client.contactPhone}
                  <CopyButton value={client.contactPhone} subject={t('otpService.detail.contact.phone', { defaultValue: 'Phone' })} />
                </dd>
              </div>
            )}
          </dl>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          tone="info"
          icon={Send}
          label={t('otpService.detail.kpi.sentToday', { defaultValue: 'Sent today' })}
          value={String(stats.sentToday)}
        />
        <KpiCard
          tone="brand"
          icon={CalendarDays}
          label={t('otpService.detail.kpi.sentThisMonth', { defaultValue: 'Sent this month' })}
          value={String(stats.sentThisMonth)}
        />
        <KpiCard
          tone="neutral"
          icon={MessageCircle}
          label={t('otpService.detail.kpi.sentAllTime', { defaultValue: 'Sent all-time' })}
          value={String(stats.sentCount)}
          hint={stats.failedCount > 0
            ? t('otpService.detail.kpi.failedCount', {
                defaultValue: '{{n}} failed',
                n: stats.failedCount,
              })
            : undefined}
        />
        <KpiCard
          tone="warning"
          icon={ReceiptText}
          label={t('otpService.detail.kpi.cost', { defaultValue: 'Cost (all-time)' })}
          value={fmtUsd(stats.cost)}
        />
        <KpiCard
          tone="success"
          icon={Wallet}
          label={t('otpService.detail.kpi.paymentsTotal', { defaultValue: 'Payments received' })}
          value={fmtUsd(stats.paymentsTotal)}
        />
        <KpiCard
          tone={balanceTone}
          icon={Coins}
          label={t('otpService.detail.kpi.balance', { defaultValue: 'Balance' })}
          value={fmtUsd(balance)}
          hint={balanceHint}
        />
      </div>

      {/* 30-day delivery chart */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
          {t('otpService.detail.usageTitle', { defaultValue: 'Last 30 days' })}
        </h2>
        {!usage ? (
          <Skeleton className="mt-3 h-64 w-full" />
        ) : chartEmpty ? (
          <div className="flex h-64 items-center justify-center text-sm text-fg-muted">
            {t('otpService.detail.usageEmpty', { defaultValue: 'No messages in this window yet.' })}
          </div>
        ) : (
          <div className="mt-2 h-64 w-full" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usage.days} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                  tickFormatter={(d: string) => d.slice(5)}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }}
                  allowDecimals={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={chartTooltipStyle()}
                  labelFormatter={(d) => String(d ?? '')}
                />
                <Bar
                  dataKey="sent"
                  name={t('otpService.detail.sent', { defaultValue: 'Sent' })}
                  stackId="day"
                  fill={CHART_COLORS.brand}
                />
                <Bar
                  dataKey="failed"
                  name={t('otpService.detail.failed', { defaultValue: 'Failed' })}
                  stackId="day"
                  fill={CHART_COLORS.danger}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Delivery settings summary */}
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-info/15 text-info">
              <Settings2 className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                {t('otpService.detail.settingsTitle', { defaultValue: 'Delivery settings' })}
              </h2>
            </div>
          </div>
          <Separator className="my-4" />
          <dl className="flex flex-col gap-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">
                {t('otpService.detail.settings.instance', { defaultValue: 'Instance' })}
              </dt>
              <dd className="flex items-center gap-1.5">
                {client.instanceName ? (
                  <>
                    <span className="font-mono text-xs">{client.instanceName}</span>
                    <CopyButton value={client.instanceName} subject={t('otpService.detail.settings.instance', { defaultValue: 'Instance' })} />
                    {instanceGone ? (
                      <Badge variant="danger">
                        {t('otpService.instanceMissing', { defaultValue: 'Missing' })}
                      </Badge>
                    ) : liveInstance ? (
                      <Badge variant={instanceStateVariant(liveInstance.state)}>
                        {t(`whatsapp.state.${liveInstance.state}`, { defaultValue: liveInstance.state })}
                      </Badge>
                    ) : null}
                  </>
                ) : (
                  <span className="text-fg-muted">
                    {t('otpService.noInstance', { defaultValue: 'Not set' })}
                  </span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">
                {t('otpService.detail.settings.price', { defaultValue: 'Price per message' })}
              </dt>
              <dd>
                {new Decimal(client.pricePerMessage || 0).isZero() ? (
                  <Badge variant="secondary">{t('otpService.free', { defaultValue: 'Free' })}</Badge>
                ) : (
                  <span dir="ltr" className="font-mono tabular-nums">{fmtUsd(client.pricePerMessage)}</span>
                )}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="shrink-0 text-fg-muted">
                {t('otpService.detail.settings.template', { defaultValue: 'Template' })}
              </dt>
              <dd className="flex min-w-0 items-start gap-1">
                <code className="min-w-0 break-words rounded bg-bg-subtle px-2 py-1 text-end text-xs">
                  {client.messageTemplate}
                </code>
                <CopyButton value={client.messageTemplate} subject={t('otpService.detail.settings.template', { defaultValue: 'Template' })} />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">
                {t('otpService.detail.settings.codeLength', { defaultValue: 'Code length' })}
              </dt>
              <dd className="font-mono tabular-nums">{client.codeLength}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">
                {t('otpService.detail.settings.ttl', { defaultValue: 'Code lifetime' })}
              </dt>
              <dd className="font-mono tabular-nums">
                {t('otpService.detail.settings.ttlSeconds', {
                  defaultValue: '{{seconds}} s',
                  seconds: client.ttlSeconds,
                })}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Customer hand-off */}
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-warning/15 text-warning">
              <BookOpen className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                {t('otpService.detail.handoffTitle', { defaultValue: 'Customer hand-off' })}
              </h2>
              <p className="mt-1 text-xs text-fg-muted">
                {t('otpService.detail.handoffHint', {
                  defaultValue:
                    'The full API key only appears when it is created or rotated — this prefix just identifies it.',
                })}
              </p>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex flex-col gap-2 text-xs">
            <CopyableCode
              label={t('otpService.detail.handoff.baseUrl', { defaultValue: 'Base URL' })}
              value={OTP_BASE_URL}
              truncate
              className="[&>span]:w-24"
            />
            <CopyableCode
              label={t('otpService.detail.handoff.keyPrefix', { defaultValue: 'Key prefix' })}
              value={client.keyPrefix}
              display={`${client.keyPrefix}…`}
              truncate
              className="[&>span]:w-24"
            />
            <CopyableCode
              label={t('otpService.detail.handoff.docs', { defaultValue: 'Docs' })}
              value={OTP_DOCS_URL}
              truncate
              className="[&>span]:w-24"
            />
          </div>
          <ShButton
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => downloadJson('vylo-otp.postman_collection.json', buildPostmanCollection())}
          >
            <Download className="h-4 w-4" />
            {t('otpService.detail.handoff.postman', { defaultValue: 'Download Postman collection' })}
          </ShButton>
          <p className="mt-1.5 text-xs text-fg-muted">
            {t('otpService.detail.handoff.postmanHint', {
              defaultValue: 'Pre-filled with the base URL — set the apiKey variable to the key you sent the customer.',
            })}
          </p>
        </Card>
      </div>

      {/* Payments */}
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-success/15 text-success">
            <Wallet className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.detail.paymentsTitle', { defaultValue: 'Payments' })}
            </h2>
          </div>
        </div>
        <Separator className="my-4" />
        <form onSubmit={(e) => void onRecordPayment(e)} className="flex flex-wrap items-center gap-2" noValidate>
          <Input
            value={payForm.amount}
            onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="10.00"
            aria-label={t('otpService.detail.payments.amount', { defaultValue: 'Amount (USD)' })}
            inputMode="decimal"
            dir="ltr"
            className="w-32 font-mono"
          />
          <Input
            value={payForm.note}
            onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))}
            placeholder={t('otpService.detail.payments.notePlaceholder', {
              defaultValue: 'wire ref / "paid in cash"',
            })}
            aria-label={t('otpService.detail.payments.note', { defaultValue: 'Note' })}
            maxLength={500}
            className="min-w-[12rem] flex-1"
          />
          <Button type="submit" loading={payBusy} disabled={!payForm.amount.trim()}>
            {t('otpService.detail.payments.record', { defaultValue: 'Record' })}
          </Button>
        </form>

        {payments === null ? (
          <Skeleton className="mt-4 h-16 w-full" />
        ) : payments.length === 0 ? (
          <p className="mt-4 text-sm text-fg-muted">
            {t('otpService.detail.payments.empty', { defaultValue: 'No payments recorded yet.' })}
          </p>
        ) : (
          <ul className="mt-4 flex flex-col divide-y divide-border rounded-lg border border-border">
            {payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <span className="text-xs text-fg-muted">
                    {formatDate(p.paidAt, { locale: i18n.language })}
                  </span>
                  <span dir="ltr" className="font-mono text-sm font-medium tabular-nums text-success">
                    {fmtUsd(p.amount)}
                  </span>
                  {p.note && <span className="truncate text-xs text-fg-muted">{p.note}</span>}
                </div>
                <ShButton
                  variant="ghost"
                  size="sm"
                  onClick={() => void onDeletePayment(p)}
                  disabled={payBusy}
                  aria-label={tc('actions.delete', { defaultValue: 'Delete' })}
                >
                  <Trash2 className="h-4 w-4 text-danger" />
                </ShButton>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Message log */}
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
            <MessageCircle className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.detail.messagesTitle', { defaultValue: 'Message log' })}
            </h2>
          </div>
        </div>
        <Separator className="my-4" />
        {messages === null ? (
          <Skeleton className="h-32 w-full" />
        ) : messages.total === 0 ? (
          <p className="text-sm text-fg-muted">
            {t('otpService.detail.messages.empty', { defaultValue: 'No messages yet.' })}
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <DataTable
              columns={messageColumns}
              data={messages.items}
              rowKey={(m) => m.id}
              paginated={false}
              searchable={false}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-3 text-xs text-fg-muted">
              <span>
                {t('otpService.detail.messages.showing', {
                  defaultValue: '{{from}}–{{to}} of {{total}}',
                  from: msgFrom,
                  to: msgTo,
                  total: messages.total,
                })}
              </span>
              <div className="flex gap-2">
                <ShButton
                  variant="outline"
                  size="sm"
                  onClick={() => setMsgPage((p) => Math.max(1, p - 1))}
                  disabled={msgPage === 1}
                >
                  {t('otpService.detail.messages.prev', { defaultValue: 'Previous' })}
                </ShButton>
                <ShButton
                  variant="outline"
                  size="sm"
                  onClick={() => setMsgPage((p) => p + 1)}
                  disabled={msgTo >= messages.total}
                >
                  {t('otpService.detail.messages.next', { defaultValue: 'Next' })}
                </ShButton>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!busy) setEditOpen(o); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t('otpService.detail.editTitle', { defaultValue: 'Edit {{name}}', name: client.name })}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void saveEdit(e)} className="flex flex-col gap-4" noValidate>
            <OtpClientForm
              values={editValues}
              onChange={(patch) => setEditValues((v) => ({ ...v, ...patch }))}
              instances={instances}
              idPrefix="otp-edit"
            />
            <DialogFooter>
              <ShButton type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>
                {tc('actions.cancel', { defaultValue: 'Cancel' })}
              </ShButton>
              <Button type="submit" loading={busy}>
                {t('otpService.detail.save', { defaultValue: 'Save' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <OtpKeyRevealDialog
        apiKey={revealKey}
        clientName={client.name}
        onDone={() => setRevealKey(null)}
      />
    </div>
  );
}
